import { Link } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { useRealtimeSync } from "@/lib/useRealtime";
import { AbsenceApprovalCard } from "@/pages/admin/zeiterfassung/PendingApprovalCards";
import { usePendingApprovals } from "@/pages/admin/zeiterfassung/usePendingApprovals";

interface HomeApprovalsCardsProps {
  enabled: boolean;
  /** Leeren Erfolgszustand ausblenden, damit die Startseite nicht aufgebläht wird. */
  hideWhenEmpty?: boolean;
}

export function HomeApprovalsCards({ enabled, hideWhenEmpty = false }: HomeApprovalsCardsProps) {
  const {
    absenceItems,
    loading,
    busyAbsenceId,
    absenceComments,
    setAbsenceComment,
    reviewAbsence,
    acknowledgeAbsence,
    reload,
  } = usePendingApprovals({
    enabled,
    loadDayViolations: false,
  });

  useRealtimeSync({
    domains: ["employee-times"],
    paused: !enabled,
    onChange: reload,
  });

  if (!enabled) return null;
  if (hideWhenEmpty && !loading && absenceItems.length === 0) return null;

  const zeiterfassungLink = (
    <Link
      to="/zeiterfassung"
      className="inline-flex items-center rounded-full border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10"
    >
      Zur Zeiterfassung
    </Link>
  );

  return (
    <Card
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          Abwesenheitsanträge
          {!loading && absenceItems.length > 0 ? (
            <span className="rounded-full bg-status-orange/15 px-2 py-0.5 text-xs font-semibold text-status-orange">
              {absenceItems.length} offen
            </span>
          ) : null}
        </span>
      }
      actions={zeiterfassungLink}
      bodyClassName="!p-0"
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : absenceItems.length === 0 ? (
        <div className="eh-home-queue">
          <div className="eh-home-empty" role="status">
            <span className="eh-home-empty__mark" aria-hidden="true">
              <CheckCircleIcon />
            </span>
            <span>
              <span className="eh-home-empty__title">Keine offenen Anträge</span>
              <span className="eh-home-empty__meta">
                Urlaub und Abwesenheiten sind freigegeben.
              </span>
            </span>
          </div>
        </div>
      ) : (
        <ul className="eh-home-queue eh-home-queue-scroll" role="list">
          {absenceItems.map((item) => (
            <li key={item.id}>
              <AbsenceApprovalCard
                item={item}
                compact
                comment={absenceComments[item.id] ?? ""}
                onCommentChange={(value) => setAbsenceComment(item.id, value)}
                busy={busyAbsenceId === item.id}
                onApprove={() => void reviewAbsence(item.id, "approve")}
                onReject={() => void reviewAbsence(item.id, "reject")}
                onAcknowledge={() => void acknowledgeAbsence(item.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
